import { useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { Nav2 } from '../Text';
import { Tooltip } from '../Tooltip';
import { Side } from '../../utils/types';

const SelectorContainer = styled.div<{ disabled: boolean }>`
  position: relative;
  width: auto;
  display: flex;
  flex-direction: row;
  background: ${({ disabled, theme }) => (disabled ? theme.bg3 : theme.bg0)};
  padding: 0.25rem;
  border-radius: 0.75rem;

  &:enabled:active > * {
    background: ${({ disabled }) => (disabled ? 'auto' : 'none')};
    border-color: rgba(0, 0, 0, 0);
  }
`;

const ButtonWrapper = styled(Nav2)<{ selected: boolean; disabled: boolean }>`
  padding: 0.44rem 2rem;
  border-radius: 0.5rem;
  border: solid 0.066rem;
  transition: all ${({ theme }) => theme.fast} ease-out;

  ${({ disabled, selected, theme }) => {
    if (disabled) {
      return `
        background: ${selected ? theme.bg4 : 'none'};
        color: ${theme.gray3};
      `;
    }
    if (selected) {
      return `
        background: ${theme.bg4};
        color: ${theme.white};
      `;
    }
    return `
      background: none;
      color: ${theme.gray3};
    `;
  }}

  &:hover {
    cursor: pointer;
  }
`;

const LongButton = styled(ButtonWrapper)<{ disabled: boolean }>`
  border-color: ${({ disabled, selected, theme }) =>
    selected && !disabled ? theme.green : 'rgba(0,0,0,0)'};

  ${({ disabled, selected, theme }) => {
    if (disabled) {
      return `
        &:hover {
          cursor: auto;
        }
      `;
    }
    return `
      &:hover {
        color: ${selected ? theme.white : theme.green};
      }

      &:enabled:active {
        background: ${selected ? 'none' : theme.gray3};
        border-color: ${selected ? 'rgba(0,0,0,0)' : theme.green};
      }
    `;
  }}
`;

const ShortButton = styled(ButtonWrapper)<{ disabled: boolean }>`
  border-color: ${({ disabled, selected, theme }) =>
    selected && !disabled ? theme.red : 'rgba(0,0,0,0)'};
  ${({ disabled, selected, theme }) => {
    if (disabled) {
      return `
        &:hover {
          cursor: auto;
        }
      `;
    }
    return `
      &:hover {
        color: ${selected ? theme.white : theme.red};
      }

      &:enabled:active {
        background: ${selected ? 'none' : theme.gray3};
        border-color: ${selected ? 'rgba(0,0,0,0)' : theme.red};
      }
    `;
  }}
`;

const TooltipWrapper = styled.div`
  position: absolute;
  right: -20%;
  top: 50%;
  transform: translateY(-50%);
  z-index: ${({ theme }) => theme.tooltipZ};
`;

interface DirectionSelectorProps {
  defaultSelected: Side;
  onChange: (newDirection: Side) => void;
  disabled?: boolean;
}

export function DirectionSelector({
  defaultSelected = Side.Long,
  onChange,
  disabled = false,
}: DirectionSelectorProps) {
  const { t } = useTranslation();

  const [direction, setDirection] = useState(defaultSelected);

  const setLong = useCallback(() => {
    if (direction !== Side.Long && !disabled) {
      setDirection(Side.Long);
      onChange(Side.Long);
    }
  }, [direction, onChange, disabled]);

  const setShort = useCallback(() => {
    if (direction !== Side.Short && !disabled) {
      setDirection(Side.Short);
      onChange(Side.Short);
    }
  }, [direction, onChange, disabled]);

  return (
    <SelectorContainer disabled={disabled}>
      <LongButton selected={direction === Side.Long} onClick={setLong} disabled={disabled}>
        {t('Long')}
      </LongButton>
      <ShortButton selected={direction === Side.Short} onClick={setShort} disabled={disabled}>
        {t('Short')}
      </ShortButton>
      <TooltipWrapper>
        <Tooltip size="l" position="bottom" offset={-0.4} disabled={disabled}>
          {t('Select long when you expect the price to rise and short when you expect it to fall.')}
        </Tooltip>
      </TooltipWrapper>
    </SelectorContainer>
  );
}

DirectionSelector.defaultProps = {
  disabled: false,
};
